import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Pain subsection adapter
 * Handles pain assessment including FLACC scale components (8 fields)
 */
export class PainAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Pain assessment fields
    const painStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS
    )?.at(0)

    const painGoal = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL
    )?.at(0)

    // FLACC Scale components (Face, Legs, Activity, Cry, Consolability)
    const flaccScaleFace = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE
    )?.at(0)

    const flaccScaleLegs = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS
    )?.at(0)

    const flaccScaleActivity = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY
    )?.at(0)

    const flaccScaleCry = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY
    )?.at(0)

    const flaccScaleConsolability = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.PAIN_STATUS]: painStatus,
      [SocFormField.PAIN_GOAL]: painGoal,
      [SocFormField.PAIN_FLACC_SCALE_FACE]: flaccScaleFace,
      [SocFormField.PAIN_FLACC_SCALE_LEGS]: flaccScaleLegs,
      [SocFormField.PAIN_FLACC_SCALE_ACTIVITY]: flaccScaleActivity,
      [SocFormField.PAIN_FLACC_SCALE_CRY]: flaccScaleCry,
      [SocFormField.PAIN_FLACC_SCALE_CONSOLABILITY]: flaccScaleConsolability,
      [SocFormField.PAIN_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS,
        value: this.getFormDataField(SocFormField.PAIN_STATUS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE,
        value: this.getFormDataField(SocFormField.PAIN_FLACC_SCALE_FACE)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS,
        value: this.getFormDataField(SocFormField.PAIN_FLACC_SCALE_LEGS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY,
        value: this.getFormDataField(SocFormField.PAIN_FLACC_SCALE_ACTIVITY)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY,
        value: this.getFormDataField(SocFormField.PAIN_FLACC_SCALE_CRY)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY,
        value: this.getFormDataField(SocFormField.PAIN_FLACC_SCALE_CONSOLABILITY)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.PAIN_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add pain goal if it exists (it is conditionally rendered when patient has pain)
    if (this.formDataFieldExists(SocFormField.PAIN_GOAL)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL,
        value: this.getFormDataField(SocFormField.PAIN_GOAL)
      })
    }

    return mutations
  }
}
